export const DEFAULT_OK_WITHIN_PCT = 10;

// Headroom past the largest number (a tenth of it), so the bar and marker
// don't touch the end
const withHeadroom = (n: number) => n + n / 10;

const DEFAULT_BANDS: BulletCharts.Bands = {
  mode: 'percentOfTarget',
  okWithinPct: DEFAULT_OK_WITHIN_PCT,
};

/**
 * The two ascending band edges, or undefined when there's nothing to band by
 * (a % of target band with no target).
 *
 * Higher is better: at or above the upper edge is good, at or above the lower
 * is OK, below it is poor. Lower is better mirrors it: at or below the lower
 * edge is good, at or below the upper is OK. An edge counts as the better band.
 */
export const getBandEdges = ({
  target,
  bands = DEFAULT_BANDS,
  lowerIsBetter,
}: {
  target?: number;
  bands?: BulletCharts.Bands;
  lowerIsBetter: boolean;
}): [number, number] | undefined => {
  if (bands.mode === 'absolute') {
    const [a, b] = bands.thresholds;
    return a <= b ? [a, b] : [b, a];
  }

  if (target === undefined) {
    return undefined;
  }

  const offset = (Math.abs(target) * bands.okWithinPct) / 100;
  return lowerIsBetter ? [target, target + offset] : [target - offset, target];
};

export const getBulletStatus = ({
  value,
  edges,
  lowerIsBetter,
}: {
  value: number | null;
  edges: [number, number] | undefined;
  lowerIsBetter: boolean;
}): BulletCharts.Status => {
  if (value === null || !edges) {
    return 'none';
  }

  const [lower, upper] = edges;
  if (lowerIsBetter) {
    if (value <= lower) {
      return 'good';
    }
    return value <= upper ? 'ok' : 'poor';
  }

  if (value >= upper) {
    return 'good';
  }
  return value >= lower ? 'ok' : 'poor';
};

/**
 * The axis: from minY (else 0, or the lowest number when below 0) to maxY
 * (else the highest number plus headroom).
 */
export const computeBulletDomain = ({
  numbers,
  minY,
  maxY,
}: {
  numbers: number[];
  minY?: number;
  maxY?: number;
}): [number, number] => {
  const finite = numbers.filter((n) => Number.isFinite(n));
  const lowest = Math.min(0, ...finite);
  const highest = Math.max(0, ...finite);

  const from = minY ?? withHeadroom(lowest);
  const to = maxY ?? withHeadroom(highest);

  return to > from ? [from, to] : [from, from + 1];
};

/**
 * The numbers a bullet's axis has to fit: its value, target and band edges
 */
export const getBulletNumbers = ({
  value,
  target,
  edges,
}: {
  value: number | null;
  target?: number;
  edges?: [number, number];
}): number[] => [
  ...(value === null ? [] : [value]),
  ...(target === undefined ? [] : [target]),
  ...(edges ?? []),
];

/**
 * Splits the domain into good / OK / poor bands. Without edges, the whole
 * domain is one neutral band.
 */
const computeBulletBands = ({
  domain,
  edges,
  lowerIsBetter,
}: {
  domain: [number, number];
  edges: [number, number] | undefined;
  lowerIsBetter: boolean;
}): BulletCharts.Band[] => {
  const [from, to] = domain;
  if (!edges) {
    return [{ from, to, status: 'none' }];
  }

  const clamp = (n: number) => Math.min(Math.max(n, from), to);
  const [lower, upper] = edges.map(clamp);
  const statuses: BulletCharts.Status[] = lowerIsBetter
    ? ['good', 'ok', 'poor']
    : ['poor', 'ok', 'good'];

  return [
    { from, to: lower, status: statuses[0] },
    { from: lower, to: upper, status: statuses[1] },
    { from: upper, to, status: statuses[2] },
  ].filter((band) => band.to > band.from);
};

export default computeBulletBands;
